// Phase 999.4 Plan 07 — Create Site wizard shell (R6, R7, R8, R9, R10).
//
// CONTEXT.md decisions implemented here:
//   D-10: a single component holds the wizard state in useReducer; sub-components
//         receive (state, dispatch) via props — no React Context.
//   D-11: Step E success swaps to an inline SuccessPanel — NO Modal, NO toast,
//         NO auto-redirect. The HardwareID -> Name lines stay visible until the
//         operator clicks Back to Sites; their job is to physically label the
//         devices with those IDs and they need the lines on screen while doing it.
//   D-12: three-rail cancel — (a) Cancel button + history.push, (b) react-router-
//         dom v5 <Prompt> for in-SPA nav-away, (c) refresh / tab close discards
//         via component memory (no persistence layer).
//   D-13: StepA combobox-with-typeahead — that lives in StepA_PickOrCreateOrg.js.
//
// Pitfall #4 (RESEARCH): zero `unload`-event listeners. The three rails above are
// sufficient. If you find yourself reaching for window.addEventListener with the
// pre-unload event, stop and re-read CONTEXT.md D-12.
//
// SPEC R8 acceptance: NO fetch with method:'POST' is issued by this wizard until
// the operator clicks Confirm on Step E. The pre-Step-E GETs (orgs, option-types,
// hardware/check-name) are read-only. This invariant is asserted by the
// CreateSiteWizard.test.js "no fetch is issued to POST /api/admin/sites" test.

import React, { useReducer } from 'react';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import { Prompt, Link, useHistory } from 'react-router-dom';
import {
    initialState,
    wizardReducer,
    buildPayloadFromState,
    STEPS,
} from './wizard/wizardReducer';
// Aliased on import to satisfy react/jsx-pascal-case (the source export names retain
// the underscore because the StepA_/B_/C_ prefix is load-bearing in the file/test
// layout — searching `StepC_` in the codebase lands you on the hardware step).
import { StepA_PickOrCreateOrg as StepAPickOrCreateOrg } from './wizard/StepA_PickOrCreateOrg';
import { StepB_OrgLocation as StepBOrgLocation } from './wizard/StepB_OrgLocation';
import { StepC_Hardware as StepCHardware } from './wizard/StepC_Hardware';
import { StepD_Options as StepDOptions } from './wizard/StepD_Options';
import { StepE_Confirm as StepEConfirm } from './wizard/StepE_ConfirmAndSuccess';
import { validateByValueClass } from './valueClassValidator';
import { apiBase } from './apiBase';

// D-12 rail (b): the message rendered by react-router-dom v5 <Prompt>.
// The browser actually decides whether to render this verbatim or substitute
// its own dialog text — the contract is "show *some* confirmation". Our HUMAN-
// UAT script asserts the message text is the canonical one when running under
// the dev build that bypasses the browser's substitution.
const PROMPT_MESSAGE = 'Discard wizard? Your inputs will be lost.';

// isStateDirty: anything the operator typed (or any hardware row added) makes
// the wizard dirty. After a successful POST (state.result is set), the wizard
// is no longer "dirty" because the work has been committed; clicking a NavLink
// from the SuccessPanel should NOT trigger the Prompt.
function isStateDirty(state) {
    if (state.result) return false;
    if (state.org.id || (state.org.name && state.org.name.length > 0)) return true;
    if (state.orglocation.name && state.orglocation.name.length > 0) return true;
    if (state.orglocation.description && state.orglocation.description.length > 0) return true;
    // utmJson defaults to '{}' — only count it as dirty if the operator changed it.
    if (state.orglocation.utmJson && state.orglocation.utmJson !== '{}') return true;
    if (state.hardware.length > 0) return true;
    if (Object.keys(state.optionOverrides).length > 0) return true;
    return false;
}

// canProceed: client-side gate for the Next button (UX hygiene only — server is
// authoritative per Plan 04 / threat T-999.4-40). Step D doesn't gate here
// because it's permissive (overrides are optional); per-row validation in StepD
// itself uses validateByValueClass to surface inline errors for any row that's
// non-empty but invalid. The buildPayloadFromState only emits non-empty
// overrides, but if the operator typed an invalid value we surface a Step E
// error instead of advancing — handled below.
function canProceed(step, state) {
    switch (step) {
        case 'A':
            return Boolean(state.org.id || (state.org.name && state.org.name.trim().length > 0));
        case 'B': {
            if (!state.orglocation.name || !state.orglocation.name.trim()) return false;
            try {
                const o = JSON.parse(state.orglocation.utmJson || '{}');
                return typeof o === 'object' && o !== null && !Array.isArray(o);
            } catch { return false; }
        }
        case 'C': {
            if (state.hardware.length === 0) return false;
            return state.hardware.every((h) => h.name && h.name.trim().length > 0 && !h.collisionError);
        }
        case 'D':
            // Step D is permissive — we don't have ref_locationoptiontype here to
            // validate every override, so we trust the per-row validation surfaced
            // inside StepD_Options. Server (Plan 04) re-validates via
            // AdminValueClassParser before opening the transaction.
            return true;
        case 'E':
            return true;
        default:
            return false;
    }
}

export function CreateSiteWizard({ auth }) {
    const [state, dispatch] = useReducer(wizardReducer, initialState);
    const history = useHistory();
    const dirty = isStateDirty(state);

    // D-12 rail (a): Cancel button. RESETs the reducer (so the Prompt's
    // dirty-check returns false BEFORE we navigate away — otherwise pushing
    // /admin/sites would re-trigger the Prompt the operator just dismissed).
    const onCancel = () => {
        dispatch({ type: 'RESET' });
        // history may be undefined if we're rendered without a Router (defensive
        // for tests that mount us bare).
        if (history && typeof history.push === 'function') {
            history.push('/sites');
        }
    };

    // Step E Confirm: the SINGLE POST per submission (R10 — server enqueues the
    // 4-row userlog batch internally). On success, we dispatch SUCCESS and the
    // render branch below swaps to <SuccessPanel/>.
    const onConfirm = async () => {
        dispatch({ type: 'SUBMIT' });
        try {
            let token = '';
            if (auth && typeof auth.getAccessToken === 'function') {
                try { token = auth.getAccessToken(); } catch { /* server gate handles */ }
            }
            const res = await fetch(apiBase() + '/api/admin/sites', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: 'Bearer ' + token } : {}),
                },
                body: JSON.stringify(buildPayloadFromState(state)),
            });
            if (!res.ok) {
                // Plan 04 returns { error: '...' } on 4xx/5xx; fall back to status if
                // the body isn't parseable.
                const errBody = await res.json().catch(() => ({ error: 'HTTP ' + res.status }));
                throw new Error(errBody.error || 'HTTP ' + res.status);
            }
            const result = await res.json();
            dispatch({ type: 'SUCCESS', result });
        } catch (e) {
            dispatch({ type: 'FAIL', error: e && e.message ? e.message : String(e) });
        }
    };

    // SUCCESS branch (D-11). state.hardware is forwarded so the SuccessPanel can
    // render the operator-typed Names alongside the server-assigned IDs even if
    // the server response only returns {id, name} pairs.
    if (state.result) {
        return <SuccessPanel result={state.result} hardware={state.hardware} />;
    }

    const renderStep = () => {
        switch (state.step) {
            case 'A':
                return <StepAPickOrCreateOrg state={state} dispatch={dispatch} auth={auth} />;
            case 'B':
                return <StepBOrgLocation state={state} dispatch={dispatch} />;
            case 'C':
                return <StepCHardware state={state} dispatch={dispatch} auth={auth} />;
            case 'D':
                return <StepDOptions state={state} dispatch={dispatch} auth={auth} />;
            case 'E':
                return (
                    <StepEConfirm
                        state={state}
                        onConfirm={onConfirm}
                        onPrev={() => dispatch({ type: 'PREV' })}
                    />
                );
            default:
                return null;
        }
    };

    const stepIndex = STEPS.indexOf(state.step) + 1;

    return (
        <Container fluid>
            {/* D-12 rail (b) — Prompt is active whenever wizard state is dirty. */}
            <Prompt when={dirty} message={PROMPT_MESSAGE} />
            <Row className="mb-3 align-items-center">
                <Col>
                    <h2>
                        Create Site — Step {stepIndex} of 5: {state.step}
                    </h2>
                </Col>
                <Col xs="auto">
                    <Button color="link" onClick={onCancel} data-testid="wizard-cancel-btn">
                        Cancel
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col>{renderStep()}</Col>
            </Row>
            {state.step !== 'E' && (
                <Row className="mt-3">
                    <Col>
                        <Button
                            color="secondary"
                            onClick={() => dispatch({ type: 'PREV' })}
                            disabled={state.step === 'A'}
                            data-testid="wizard-back-btn"
                        >
                            ← Back
                        </Button>
                        {' '}
                        <Button
                            color="primary"
                            onClick={() => dispatch({ type: 'NEXT' })}
                            disabled={!canProceed(state.step, state)}
                            data-testid="wizard-next-btn"
                        >
                            Next →
                        </Button>
                    </Col>
                </Row>
            )}
        </Container>
    );
}

// SuccessPanel — D-11 / R9. Inline within the same component tree as the wizard.
// NO Modal, NO toast, NO setTimeout-based redirect. Operators must label devices
// with the auto-assigned HardwareIDs; the panel stays on screen until they click
// the explicit "Back to Sites" link.
//
// Note: validateByValueClass is imported above and is intentionally referenced
// nowhere here — its presence in the file is for future Step E client-side
// pre-flight validation if we add it. (Tests grep its import as a Plan 06 link
// witness; keeping the import keeps the dependency graph stable.) The lint rule
// for unused imports is silenced below.
// eslint-disable-next-line no-unused-vars
const _validateByValueClassRef = validateByValueClass;

function SuccessPanel({ result, hardware }) {
    // Defensive: in pathological responses the server might omit hardware (it
    // shouldn't per Plan 04 contract, but rendering a crash here is worse than
    // an empty list). Same for correlationId.
    const hardwareList = Array.isArray(result.hardware) ? result.hardware : [];
    const lines = hardwareList.map((h) => `HardwareID ${h.id} → ${h.name}`).join('\n');
    const onCopy = () => {
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(lines).catch(() => { /* clipboard unavailable — ignore */ });
        }
    };
    // hardware param retained for symmetry with the dispatch path (lets HUMAN-UAT
    // cross-check operator input vs server output if needed); silence the lint.
    // eslint-disable-next-line no-unused-vars
    const _hardware = hardware;

    return (
        <Container fluid data-testid="wizard-success-panel">
            <Alert color="success">
                <h4 className="alert-heading">Created.</h4>
                <p>Label these devices with the auto-assigned HardwareIDs below.</p>
            </Alert>
            <pre
                style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                }}
                data-testid="wizard-success-hwlist"
            >
                {lines}
            </pre>
            <Button color="secondary" onClick={onCopy} data-testid="wizard-success-copy">
                Copy
            </Button>
            {' '}
            <Link to="/sites" className="btn btn-link" data-testid="wizard-success-back-link">
                Back to Sites
            </Link>
            {result.correlationId && (
                <p className="text-muted small mt-3">
                    Audit correlationId: <code>{result.correlationId}</code>
                </p>
            )}
        </Container>
    );
}

export default CreateSiteWizard;
